import React, { useState } from 'react';
import { Text } from '../../UI/Typography';
import { Button } from '../../UI/Buttons';
import Table from '../../UI/Table';
import { deleteUser } from '../../../Services/Api/userService';
import { usePopup } from '../../../Popups/PopupContext';
import { FaEdit, FaTrashAlt, FaSpinner } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const UserTable = ({ users, onRefresh, onEdit }) => {
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { showSuccess, showError } = usePopup();

  const handleEditClick = (user) => {
    if (onEdit) {
      onEdit(user);
    }
  };

  const handleDeleteClick = (userId, username) => {
    setConfirmDelete({ userId, username });
  };

  const confirmDeleteUser = async () => {
    if (!confirmDelete) return;
    
    const { userId, username } = confirmDelete;
    setDeletingUserId(userId);
    
    try {
      await deleteUser(userId);
      if (onRefresh) onRefresh();
      
      showSuccess(
        'User Deleted', 
        `User "${username}" has been successfully deleted.`
      );
    } catch (error) {
      showError(
        'Delete Failed', 
        `Error deleting user: ${error.message}`
      );
    } finally {
      setDeletingUserId(null);
      setConfirmDelete(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const columns = [
    { key: 'id', label: 'ID', align: 'left' },
    { key: 'username', label: 'Username', align: 'left' },
    { key: 'first_name', label: 'First Name', align: 'left' },
    { key: 'last_name', label: 'Last Name', align: 'left' },
    { key: 'email', label: 'Email', align: 'left' },
    { key: 'role', label: 'Role', align: 'left' },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      width: '120px'
    }
  ];

  const renderCell = (user, column) => {
    if (column.key === 'actions') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          <Button
            title={`Edit user ${user.username}`}
            onClick={() => handleEditClick(user)}
            disabled={deletingUserId === user.id}
            variant="ghost"
            size="small"
          >
            <FaEdit />
          </Button>
          <Button
            title={`Delete user ${user.username}`}
            onClick={() => handleDeleteClick(user.id, user.username)}
            disabled={deletingUserId === user.id}
            variant="ghost"
            size="small"
            color="error"
          >
            {deletingUserId === user.id ? <FaSpinner className="spin" /> : <FaTrashAlt />}
          </Button>
        </div>
      );
    }
    if (column.key === 'role') {
      if (Array.isArray(user.role)) {
        return user.role.length > 0 ? user.role.join(' | ') : 'N/A';
      }
      return user.role || 'N/A';
    }
    return user[column.key] || 'N/A';
  };

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        data={users || []}
        renderCell={renderCell}
        emptyState={
          <Text style={{ padding: '2rem', textAlign: 'center' }}>
            No users found.
          </Text>
        }
      />
      
      {/* Animated Confirmation Popup */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={cancelDelete}
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0, y: -100 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.7, opacity: 0, y: -100 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>
                Confirm Delete
              </h3>
              <p style={{ margin: '0 0 24px 0', color: '#666' }}>
                Are you sure you want to delete the user "{confirmDelete.username}" (ID: {confirmDelete.userId})? 
                This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button 
                  variant="secondary" 
                  onClick={cancelDelete}
                  style={{
                    background: '#28A745',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={confirmDeleteUser}
                  style={{ 
                    background: '#DC3545',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserTable;